import {
	ALL_PERMISSIONS,
	DOCUMENTS_AUTOFLAG_CONFIGURE,
	DOCUMENTS_READ,
	DOCUMENTS_WRITE,
	LOTS_READ,
	LOTS_WRITE,
	NOTIFICATIONS_READ,
	NOTIFICATIONS_WRITE,
	ONBOARDING_READ,
	ONBOARDING_WRITE,
	PARTIES_READ,
	PARTIES_WRITE,
	type Permission,
	SETTINGS_INTEGRATIONS_READ,
	SETTINGS_INTEGRATIONS_WRITE,
	SETTINGS_REGISTRY_READ,
	SETTINGS_REGISTRY_WRITE,
	SETTINGS_ROLES_READ,
	SETTINGS_ROLES_WRITE,
	SHIPMENTS_EXPORT,
	SHIPMENTS_READ,
	SHIPMENTS_WRITE,
	USERS_READ,
	USERS_WRITE,
} from "./permissions.js"

export interface PermissionItem {
	readonly key: Permission
	readonly label: string
	readonly description: string
}

export interface PermissionGroup {
	readonly id: string
	readonly label: string
	readonly items: ReadonlyArray<PermissionItem>
}

export const PERMISSION_GROUPS: ReadonlyArray<PermissionGroup> = [
	{
		id: "shipments",
		label: "Shipments",
		items: [
			{
				key: SHIPMENTS_READ,
				label: "Read",
				description: "View shipment list, search, and detail pages.",
			},
			{
				key: SHIPMENTS_WRITE,
				label: "Write",
				description: "Create + edit shipments, lots, parties, corrections.",
			},
			{
				key: SHIPMENTS_EXPORT,
				label: "Export",
				description: "Download XLSX exports of shipment data.",
			},
		],
	},
	{
		id: "documents",
		label: "Documents",
		items: [
			{ key: DOCUMENTS_READ, label: "Read", description: "View attached docs + due-date timers." },
			{ key: DOCUMENTS_WRITE, label: "Write", description: "Upload, edit, and delete documents." },
			{
				key: DOCUMENTS_AUTOFLAG_CONFIGURE,
				label: "Configure auto-flagging",
				description: "Per-doctype auto-flag toggle (F2 L2).",
			},
		],
	},
	{
		id: "lots",
		label: "Lots",
		items: [
			{ key: LOTS_READ, label: "Read", description: "View lots." },
			{ key: LOTS_WRITE, label: "Write", description: "Create + edit lots." },
		],
	},
	{
		id: "parties",
		label: "Parties",
		items: [
			{ key: PARTIES_READ, label: "Read", description: "View parties." },
			{ key: PARTIES_WRITE, label: "Write", description: "Create + edit parties." },
		],
	},
	{
		id: "notifications",
		label: "Notifications",
		items: [
			{ key: NOTIFICATIONS_READ, label: "Read", description: "View own notifications inbox." },
			{
				key: NOTIFICATIONS_WRITE,
				label: "Write",
				description: "Mark-read / dismiss own notifications.",
			},
		],
	},
	{
		id: "onboarding",
		label: "Onboarding",
		items: [
			{ key: ONBOARDING_READ, label: "Read", description: "View first-run wizard state." },
			{ key: ONBOARDING_WRITE, label: "Write", description: "Complete first-run wizard steps." },
		],
	},
	{
		id: "settings",
		label: "Settings (admin)",
		items: [
			{
				key: SETTINGS_REGISTRY_READ,
				label: "Registries — Read",
				description: "View carriers / lanes / ports / verticals / doctypes.",
			},
			{
				key: SETTINGS_REGISTRY_WRITE,
				label: "Registries — Write",
				description: "Create + edit registry entries.",
			},
			{
				key: SETTINGS_INTEGRATIONS_READ,
				label: "Integrations — Read",
				description: "View carrier-API connection state.",
			},
			{
				key: SETTINGS_INTEGRATIONS_WRITE,
				label: "Integrations — Write",
				description: "Configure carrier integrations.",
			},
			{ key: SETTINGS_ROLES_READ, label: "Roles — Read", description: "View roles + permissions." },
			{
				key: SETTINGS_ROLES_WRITE,
				label: "Roles — Write",
				description: "Create + edit + delete custom roles.",
			},
		],
	},
	{
		id: "users",
		label: "Users (admin)",
		items: [
			{ key: USERS_READ, label: "Read", description: "View users + role assignments." },
			{ key: USERS_WRITE, label: "Write", description: "Provision + edit users + assign roles." },
		],
	},
]

// Fail fast at module load if a new permission key was added to
// permissions.ts without being grouped here.
const grouped = new Set<string>(PERMISSION_GROUPS.flatMap((g) => g.items.map((i) => i.key)))
const ungrouped = ALL_PERMISSIONS.filter((p) => !grouped.has(p))
if (ungrouped.length > 0) {
	throw new Error(`permission-groups.ts is missing entries for: ${ungrouped.join(", ")}`)
}
